"use client"

import { useCallback, useEffect, useState } from 'react'
import {
  canonicalizeToolGrassType,
  canonicalizeToolLawnSize,
  canonicalizeToolSunlight,
  normalizeGrassType,
} from '@/lib/lawn-plan-engine'

const STORAGE_KEY = 'lawn-care-saved-inputs'

export interface SavedInputs {
  zip?: string
  grassType?: string
  lawnSize?: number
  soilType?: string
  season?: string
  sunlight?: string
  source?: string
  updatedAt?: string
}

/** Synchronous read of the saved ZIP, for callers that must know before the
 * hook's mount effect has loaded storage. Returns only a five-digit ZIP. */
export function readSavedZip(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    const zip = (JSON.parse(stored) as SavedInputs).zip
    return typeof zip === 'string' && /^\d{5}$/.test(zip) ? zip : null
  } catch {
    return null
  }
}

/**
 * Persists common lawn care inputs to localStorage so they carry
 * across tool pages for anonymous users. URL params and saved lawn
 * profiles take priority over these values.
 */
export function useSavedInputs() {
  const [savedInputs, setSavedInputs] = useState<SavedInputs>({})

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        setSavedInputs(JSON.parse(stored))
      }
    } catch {
      // Ignore parse errors
    }
  }, [])

  // Save specific fields (merges with existing)
  const saveInputs = useCallback((inputs: Partial<SavedInputs>) => {
    setSavedInputs(prev => {
      const merged = { ...prev }
      // Only update fields that have truthy values
      for (const [key, value] of Object.entries(inputs)) {
        if (value !== undefined && value !== null && value !== '') {
          if (key === 'grassType' && typeof value === 'string') {
            const resolved = normalizeGrassType(value)
            if (resolved.isKnown) {
              merged.grassType = canonicalizeToolGrassType(value)
            }
            continue
          }
          if (key === 'lawnSize') {
            const sqft = canonicalizeToolLawnSize(value as string | number)
            if (sqft) merged.lawnSize = sqft
            continue
          }
          if (key === 'sunlight' && typeof value === 'string') {
            const sunlight = canonicalizeToolSunlight(value)
            if (sunlight) merged.sunlight = sunlight
            continue
          }
          ;(merged as any)[key] = value
        }
      }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
      } catch {
        // Ignore quota errors
      }
      return merged
    })
  }, [])

  // Remove specific fields (persists the removal)
  const clearInputs = useCallback((keys: Array<keyof SavedInputs>) => {
    setSavedInputs(prev => {
      const merged = { ...prev }
      for (const key of keys) delete merged[key]
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
      } catch {
        // Ignore quota errors
      }
      return merged
    })
  }, [])

  return { savedInputs, saveInputs, clearInputs }
}
